/**
 * The offline capture queue — an IndexedDB store of readings taken while the
 * line had no network, and the rules that decide whose they are.
 *
 * ⚠ WHY THIS FILE IS NOT A THIN WRAPPER OVER IndexedDB.
 *
 * app-shiftboard's queue stored a payload and nothing else. `replay()` then
 * POSTed each item with a token minted for whoever was signed in AT REPLAY
 * TIME, and the server stamps the author from that bearer. So a queue that
 * outlived an outage routinely outlived the account that filled it: Night keys
 * offline, hands over, Morning signs in, the queue drains, and the store records
 * Morning as the author of Night's work. It reached the database looking like
 * ordinary data, with nothing on any screen saying otherwise.
 *
 * app-quality has NO Person table. `Reading.CapturedByUpn` comes from the
 * validated token and from nowhere else, which means attribution rests entirely
 * on which account the queue drains under. Inheriting that defect here would not
 * be a reporting error — it would put the wrong person's name on a controlled
 * food-safety record.
 *
 * THE PATTERN, and you need both halves:
 *
 *   1. The UPN is stamped at ENQUEUE, inside the write, read from the same
 *      account the token will later be minted against. It is applied last so it
 *      overwrites anything a call site passed: attribution is read from the
 *      session, never asserted by a caller.
 *   2. Replay sends only items whose stamp matches the account signed in now.
 *      A foreign item is HELD — never re-attributed, never silently dropped.
 *
 * THE RULINGS BEHIND THE BEHAVIOUR HERE:
 *
 *   · A held item stays on the device indefinitely until its own account signs
 *     in here again, then sends itself. The only other exit is an explicit,
 *     confirmed human discard naming the owner. There is deliberately no
 *     "send it as me" — that is the defect with a confirm dialog on it.
 *   · Held items are SKIPPED, not head-of-line blocking. FIFO only ever matters
 *     inside one account's own stream; blocking the head would strand the
 *     supervisor standing at the machine behind a colleague's orphan.
 *   · An UNSTAMPED row matches nobody and is held. On a system of controlled
 *     records, "unattributable" must never resolve to "whoever is standing here".
 *   · Replay with nobody signed in holds QUIETLY. A wall display or a boot-time
 *     replay must not raise a conflict banner about a queue that is simply
 *     waiting for someone to sign in.
 *   · HTTP 200 deletes the row whether the server ACCEPTED or REJECTED the
 *     reading: the server has it either way, and a REJECTED reading shows as a
 *     red row in the run view rather than vanishing. Network failure, 5xx, 401
 *     and 403 all KEEP the row.
 *   · A PHOTO (migration 034) rides behind its reading, in its own `photos`
 *     store: written in the reading's transaction (or after, for the walk-around),
 *     stamped the same way, held the same way, told the reading's id before the
 *     reading's row is deleted, and counted in `queued` until it has gone. A
 *     REJECTED reading keeps its photo; a capture with no row loses it.
 *
 * Dependencies are injected so every one of those rules has a test that needs
 * no browser, no MSAL and no network.
 */
import { apiUrl } from '../config/api';

/* ── The wire shape ───────────────────────────────────────
 * Mirrors CaptureInput in api/src/domain/ingestGate.ts, minus `capturedByUpn`.
 * We deliberately do not send the stamp: the server takes the UPN from the
 * token, and a body-supplied UPN that disagrees writes an ATTRIBUTION_MISMATCH
 * event. Sending it would either be redundant (it agrees) or would be us asking
 * the server to record a mismatch we already refused to create.
 */
export interface CapturePayload {
  clientCaptureId: string;
  runId: number;
  specParameterId: number;
  meterId: number | null;
  values: number[];
  /** When the OPERATOR took the reading, by the device clock. Never conflated
   *  with ReceivedAtServer, which the database stamps on arrival — for a queued
   *  reading those two can be hours apart, and the gap is the whole point. */
  capturedAtDevice: string;
  captureMode: 'ONLINE' | 'QUEUED';
  /** Device clock minus the last server clock we saw, in ms. `null` means no
   *  server has been seen since this device booted. That is not the same as 0
   *  ("device and server agree") and the difference is exactly what someone
   *  later auditing a queued reading's timestamp needs to know. */
  clockOffsetMs: number | null;
  evidenceRef: string | null;
}

/**
 * What the server says about one capture — one entry of the `outcomes` array
 * `quality_reading_capture` answers with.
 *
 * Everything but the id is optional because the server has TWO answers, not one.
 * A reading it judged carries `ingestVerdict` (mirroring CaptureResult); a
 * reading whose payload it refused before the gate ever saw it carries `error`
 * and nothing else. Typing the judged fields as always-present would make the
 * run view read `ingestVerdict` off an item that has none.
 */
export interface CaptureOutcome {
  clientCaptureId: string;
  readingId?: number;
  ingestVerdict?: 'ACCEPTED' | 'REJECTED';
  verdict?: 'IN' | 'ACTION' | 'OUT' | null;
  aggregate?: number | null;
  rejectReason?: string | null;
  message?: string | null;
  duplicate?: boolean;
  /** The server refused the PAYLOAD — a malformed item that never reached the
   *  gate and was never stored. The row still leaves the queue, because
   *  re-sending a body the server cannot parse only repeats the refusal, so this
   *  sentence is the only trace of it and has to be shown. */
  error?: string;
}

/** Enough to name a held capture in the banner without refetching anything —
 *  a device that is offline cannot look up what run 412 was. Display only. */
export interface CaptureContext {
  lineCode?: string;
  productCode?: string;
  parameterCode?: string;
  parameterName?: string;
  runLabel?: string;
}

/** What a call site hands to `enqueue`. No identity, no id, no clock: the queue
 *  stamps all three, which is why none of them appear here. */
export interface CaptureDraft {
  runId: number;
  specParameterId: number;
  values: number[];
  meterId?: number | null;
  evidenceRef?: string | null;
  context?: CaptureContext;
  /** Photos of what was seen (migration 034), written in the SAME transaction as
   *  the reading: the keypad clears its state the moment focus moves, so a photo
   *  chosen before Commit has nowhere else to live. Optional, always. */
  photos?: readonly CapturePhoto[];
  /** Not a field a caller fills in. Attribution is read from the session inside
   *  the write below; typing it `never` makes an attempt to supply one a compile
   *  error, and `enqueue` ignores it at runtime anyway. */
  upn?: never;
  /** Same again. The queue mints this so a retry cannot change it. */
  clientCaptureId?: never;
}

/** One row of the IndexedDB store. */
export interface QueueRow {
  /** Assigned by IndexedDB's autoIncrement, so `all()` is FIFO by construction. */
  id: number;
  /** The account that KEYED this, normalised. `''` means unattributable, which
   *  matches nobody — including whoever is signed in now. */
  upn: string;
  reading: CapturePayload;
  context: CaptureContext;
  enqueuedAt: string;
}

/** A photo handed to the queue: already scaled to a JPEG on the device (photoScale.ts). */
export interface CapturePhoto {
  blob: Blob;
  capturedAtDevice?: string;
}

/**
 * A photo of what was seen, waiting to go with (after) its reading (migration 034).
 *
 * Keyed to the capture's client id until the server answers for the reading;
 * replay records the server's `readingId` on it BEFORE the reading's row is
 * deleted, because that is the only moment the device learns it. Stamped with
 * the account signed in when it was taken, exactly as the reading is: a photo
 * of another account's is HELD with its reading, never sent as somebody else.
 * A sent photo is deleted (the blob has no business staying on the device), so
 * a stored row is only ever `pending` or `refused`.
 */
export interface QueuePhotoRow {
  photoKey: string;
  clientCaptureId: string | null;
  readingId: number | null;
  upn: string;
  blob: Blob;
  capturedAtDevice: string;
  status: 'pending' | 'sent' | 'refused';
  /** The server's machine code when it refused the photo. */
  reason: string | null;
  /** The server's sentence when it refused the photo. */
  message: string | null;
  context: CaptureContext;
}

/** A photo the server did not take: "Photo not sent", with Send again / Discard. */
export interface PhotoRefusal {
  photoKey: string;
  parameterName: string | null;
  reason: string | null;
  message: string;
}

/** A row that will not send under the current account, described for the banner. */
export interface HeldItem {
  /** The QUEUE row's id, deliberately not `id`: a reading has its own id, and a
   *  field called `id` sitting next to one is how the two get confused. */
  queueId: number;
  /** `''` when the row carries no stamp — say so, never guess. */
  upn: string;
  clientCaptureId: string;
  capturedAtDevice: string;
  context: CaptureContext;
}

/**
 * A capture the server took and REFUSED.
 *
 * The reading is not lost — `IngestVerdict = REJECTED` with a `RejectReason` is
 * stored server-side, which is rule 4. What was lost was any way for the person
 * who took it to find that out, which is rule 4's client half.
 */
export interface QueueRefusal {
  clientCaptureId: string;
  /** The parameter, when the row carried one. The operator thinks in checks. */
  parameterCode: string | null;
  /** The parameter's name, when the row carried one. Shown before the code:
   *  "SUCTION_CUPS_DUAL_MACHINE_4_AT_DOY_B" is not what the operator calls it. */
  parameterName: string | null;
  /** The server's machine code — `no_current_grant`, `readiness_outstanding`. */
  reason: string | null;
  /** The server's sentence, which is written for the person at the line. */
  message: string | null;
  at: number;
}

export interface QueueSnapshot {
  /** Items belonging to the CURRENT account. Every "is anything pending?"
   *  question counts these and only these — a colleague's held orphan is not
   *  this operator's problem and must not read as their backlog.
   *
   *  ⚠⚠ `null` MEANS THE STORE COULD NOT BE READ — NOT ZERO, AND THAT IS WHY THIS
   *  IS NULLABLE RATHER THAN A FLAG BESIDE IT. It used to answer 0 when
   *  IndexedDB threw (private browsing, a corrupt or full store), so the sync
   *  chip said "synced" and the Hand over button unlocked on a device that could
   *  not see its own queue. An operator handed the iPad over believing their
   *  readings were sent; the replay loop already refused to make that claim —
   *  it catches the identical rejection and stops with `store_unavailable`
   *  rather than publishing an empty queue — and `snapshot()` handed the false
   *  zero straight to the screen anyway.
   *
   *  A flag would have been ignorable. A null makes every reader decide at the
   *  type level, which is the only reason all four of them now do.
   *
   *  `held` is `[]` in this state for the same reason and is equally unknown; the
   *  chip says the queue is unreadable, which is the signal that covers both. */
  queued: number | null;
  held: HeldItem[];
  /** Captures the server refused since the operator last acknowledged them.
   *
   *  ⚠ NOT DERIVED FROM STORAGE. The row is deleted once the server has answered
   *  definitively, so by the time anything could read it back the evidence is
   *  gone. This list is the only trace on the device, which is why it is carried
   *  in the snapshot every subscriber already receives rather than through a
   *  callback somebody has to remember to supply. */
  refusals: QueueRefusal[];
  /** Refusals beyond the cap. A banner that silently truncates reads as "three
   *  were refused" when two hundred were. */
  refusalsDropped: number;
  /** This account's photos still to send, or `null` when unknown. ALREADY
   *  COUNTED IN `queued` — Hand over and Close run must not let a photo be left
   *  behind on a device whose next user cannot send it. Exposed apart so a
   *  screen waiting on READINGS can subtract it. */
  photosPending: number | null;
  /** This account's photos the server refused. */
  photosRefused: PhotoRefusal[];
  /** Other accounts' photos, waiting for their owner like their readings. */
  photosHeld: number;
}

export type ReplayStop =
  | 'drained'
  | 'nobody_signed_in'
  | 'nothing_for_this_account'
  | 'network'
  | 'server_error'
  | 'auth'
  | 'already_running'
  | 'store_unavailable';

export interface ReplaySummary extends QueueSnapshot {
  sent: number;
  stoppedBecause: ReplayStop;
}

/* ── Injected dependencies ────────────────────────────── */

/** The persistence the queue needs, reduced to three operations. IndexedDB's
 *  real API is event-based and untestable without a browser; this is the part
 *  the rules actually touch. */
export interface QueueStore {
  /** Returns the id IndexedDB assigned. The photos, if any, go in the SAME
   *  transaction: a reading and the photo of it are kept together or not at all. */
  add(row: Omit<QueueRow, 'id'>, photos?: QueuePhotoRow[]): Promise<number>;
  /** FIFO — ascending id, which is insertion order. */
  all(): Promise<QueueRow[]>;
  delete(id: number): Promise<void>;
  /** The photos store (DB_VERSION 2), its own — never the IIR queue's. */
  allPhotos(): Promise<QueuePhotoRow[]>;
  putPhoto(row: QueuePhotoRow): Promise<void>;
  deletePhoto(photoKey: string): Promise<void>;
}

/** Where identity comes from. Both halves must read the SAME account, or the
 *  stamp and the token can disagree and we are back to the original defect. */
export interface QueueIdentity {
  /** The MSAL active account's username, or `''` when nobody is signed in. */
  getUpn(): string;
  /** An access token for the Quality API, or `null`. */
  getToken(): Promise<string | null>;
}

export interface QueueDeps {
  idb: QueueStore;
  getUpn: () => string;
  getToken: () => Promise<string | null>;
  fetch: typeof fetch;
  now: () => number;
  newId: () => string;
  endpoint: string;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
  /** Called with the server's answer for each item that got one, so the run view
   *  can turn a REJECTED reading red instead of letting it disappear. */
  onOutcome: (outcome: CaptureOutcome | null, row: QueueRow, status: number) => void;
}

export interface CaptureQueue {
  enqueue(draft: CaptureDraft): Promise<QueueRow>;
  replay(): Promise<ReplaySummary>;
  snapshot(): Promise<QueueSnapshot>;
  /** How many items the CURRENT account still owes the server, or `null` when
   *  the store could not be read. A caller gating a handover on this must treat
   *  null as "do not let them", never as zero. */
  pendingForMe(): Promise<number | null>;
  /** The one non-automatic exit for a held item. `ownerUpn` must match the
   *  row's stamp: a list that re-ordered under a supervisor's finger must not be
   *  able to discard the row next to the one they meant. */
  discardHeld(queueId: number, ownerUpn: string): Promise<void>;
  subscribe(listener: (snapshot: QueueSnapshot) => void): () => void;
  /** Clear the refusal list once a person has actually seen it. Takes the ids
   *  it is dismissing, so a refusal that arrived while the banner was on screen
   *  is not cleared unread — the same reason discardHeld names its owner. */
  acknowledgeRefusals(clientCaptureIds: string[]): void;
  /** A photo taken AFTER the answer was queued (the walk-around: the answer is
   *  enqueued on the tap). `target` is the capture's client id while the reading
   *  may still be on the device, or the server's reading id once it is not.
   *  Stamped with the account signed in now, as a reading is. */
  addPhoto(target: string | number, photo: CapturePhoto, context?: CaptureContext): Promise<QueuePhotoRow>;
  /** A refused photo, sent again by its owner. */
  retryPhoto(photoKey: string, ownerUpn: string): Promise<void>;
  /** An unsent photo dropped by its owner. The reading is untouched. */
  discardPhoto(photoKey: string, ownerUpn: string): Promise<void>;
  /** Arm the backoff retry. Idempotent — the pending timer is replaced. */
  scheduleReplay(): void;
  /** Back to 2s. Called on a successful send and by the `online` event. */
  resetBackoff(): void;
  /** For tests and for the chip: the delay the next scheduleReplay would use. */
  backoffMs(): number;
}

/* ── Constants ────────────────────────────────────────── */

const DB_NAME = 'quality_queue';
/** Version 2 (migration 034): the photos store, beside the captures. */
const DB_VERSION = 2;
const STORE_NAME = 'captures';
const PHOTO_STORE = 'photos';

/** What a capture says when another tab still holds the store at an older version. */
export const QUEUE_BLOCKED_MESSAGE =
  'Quality is open in another tab or window on this device, on an older version. Close it, then try again. Nothing was recorded.';

const BACKOFF_BASE_MS = 2_000;
const BACKOFF_CAP_MS = 60_000;

/* ── Identity registration ────────────────────────────── */

/**
 * The MSAL instance lives in `config/auth.ts` and constructs itself at import
 * time against `window.location`. Importing it here would drag a browser into
 * every test of these rules, so identity is registered from outside instead —
 * `useQueue.ts` does it, unconditionally, on mount.
 *
 * Until something does, `getUpn()` answers `''`. That is the fail-closed default
 * on purpose: an unwired queue holds everything quietly rather than draining
 * under whatever token happens to be lying around. It also means a queue nobody
 * registered an identity for never sends — which is why the registration is a
 * default in the hook and not something a shell has to remember.
 */
let registeredIdentity: QueueIdentity | null = null;

export function setQueueIdentity(identity: QueueIdentity | null): void {
  registeredIdentity = identity;
}

/** Normalised: Entra hands back mixed case and UPNs are not case-sensitive, so
 *  a stamp of `Ajay.Kumar@Majans.com` must match a sign-in as `ajay.kumar@…`. */
export function normaliseUpn(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/** A row's owner. An absent or blank stamp answers `''`, which matches nobody. */
export function rowUpn(row: Pick<QueueRow, 'upn'> | null | undefined): string {
  return normaliseUpn(row?.upn);
}

/** Deliberately false when `me` is `''`: signed out matches nothing, rather
 *  than matching every unstamped row on the device. */
export function mineToSend(row: Pick<QueueRow, 'upn'>, me: string): boolean {
  return me !== '' && rowUpn(row) === me;
}

/* ── The real IndexedDB store ─────────────────────────── */

/**
 * `keyPath: 'id', autoIncrement: true` is load-bearing: the ids it hands out are
 * insertion order, so `all()` is FIFO without storing or trusting a sequence
 * number of our own.
 */
export function indexedDbStore(): QueueStore {
  let dbPromise: Promise<IDBDatabase> | null = null;

  const open = (): Promise<IDBDatabase> => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      let blocked = false;
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        }
        if (!request.result.objectStoreNames.contains(PHOTO_STORE)) {
          request.result.createObjectStore(PHOTO_STORE, { keyPath: 'photoKey' });
        }
      };
      request.onsuccess = () => {
        const db = request.result;
        // Too late: the caller was already told, and the next call opens afresh.
        if (blocked) {
          db.close();
          return;
        }
        // ⚠ A NEWER VERSION OF THIS APP, IN ANOTHER TAB, WANTS TO UPGRADE THE STORE. Let go of it,
        // or that tab's open waits for ever and every capture there hangs with no error. This tab
        // then reopens on its next call, and is told plainly if its version is the older one.
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        resolve(db);
      };
      // ⚠ ANOTHER TAB HOLDS THE STORE OPEN AT THE OLDER VERSION (one loaded before a deploy, from
      // before this handler existed). Without this the open neither succeeds nor fails, and
      // enqueue awaits for ever: the reading is never written and nothing says so. Refuse with a
      // sentence instead, and forget the attempt so the next call tries again once that tab is closed.
      request.onblocked = () => {
        blocked = true;
        dbPromise = null;
        reject(new Error(QUEUE_BLOCKED_MESSAGE));
      };
      request.onerror = () => {
        dbPromise = null;
        reject(request.error);
      };
    });
    return dbPromise;
  };

  /**
   * The database is awaited BEFORE the transaction is opened, and the request is
   * issued in the same synchronous step as `db.transaction(...)`.
   *
   * That ordering is the whole point of this helper. An IndexedDB transaction
   * goes inactive as soon as control returns to the event loop, so any `await`
   * between opening one and issuing its first request turns a write into
   * TransactionInactiveError — intermittently, on a real device, in the one code
   * path whose failure means a captured reading was never stored.
   */
  const run = async <T>(
    mode: IDBTransactionMode,
    issue: (store: IDBObjectStore) => IDBRequest<T>,
    storeName: string = STORE_NAME,
  ): Promise<T> => {
    const db = await open();
    return new Promise<T>((resolve, reject) => {
      const request = issue(db.transaction(storeName, mode).objectStore(storeName));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  };

  return {
    async add(row, photos = []) {
      if (photos.length === 0) {
        return Number(await run('readwrite', (s) => s.add(row) as IDBRequest<IDBValidKey>));
      }
      // One transaction over both stores, every request issued in the same
      // synchronous step (see `run`): the reading and its photos land together,
      // or the transaction aborts and neither does.
      const db = await open();
      return new Promise<number>((resolve, reject) => {
        const tx = db.transaction([STORE_NAME, PHOTO_STORE], 'readwrite');
        const request = tx.objectStore(STORE_NAME).add(row);
        for (const photo of photos) tx.objectStore(PHOTO_STORE).put(photo);
        tx.oncomplete = () => resolve(Number(request.result));
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error ?? new Error('The capture was not stored.'));
      });
    },
    async allPhotos() {
      return (await run('readonly', (s) => s.getAll() as IDBRequest<QueuePhotoRow[]>, PHOTO_STORE)) ?? [];
    },
    async putPhoto(photo) {
      await run('readwrite', (s) => s.put(photo), PHOTO_STORE);
    },
    async deletePhoto(photoKey) {
      await run('readwrite', (s) => s.delete(photoKey), PHOTO_STORE);
    },
    async all() {
      const stored = await run('readonly', (s) => s.getAll() as IDBRequest<QueueRow[]>);
      return (stored ?? []).slice().sort((a, b) => a.id - b.id);
    },
    async delete(id) {
      await run('readwrite', (s) => s.delete(id));
    },
  };
}

/* ── The queue ────────────────────────────────────────── */

export function createCaptureQueue(overrides: Partial<QueueDeps> = {}): CaptureQueue {
  const deps: QueueDeps = {
    idb: overrides.idb ?? indexedDbStore(),
    getUpn: overrides.getUpn ?? (() => normaliseUpn(registeredIdentity?.getUpn())),
    getToken: overrides.getToken ?? (async () => (await registeredIdentity?.getToken()) ?? null),
    fetch: overrides.fetch ?? ((...args) => globalThis.fetch(...args)),
    now: overrides.now ?? (() => Date.now()),
    newId: overrides.newId ?? (() => crypto.randomUUID()),
    // Through config/api.ts, never assembled here. That file owns the one rule
    // that matters — the base is the Function App's own host, because an SWA
    // custom domain does not forward /api — and it is also the file that knows
    // to point at localhost:7071 in dev. A second base built in this module
    // would send queued captures to production from a developer's machine.
    endpoint: overrides.endpoint ?? apiUrl('/readings'),
    setTimer: overrides.setTimer ?? ((fn, ms) => setTimeout(fn, ms)),
    clearTimer: overrides.clearTimer ?? ((handle) => clearTimeout(handle as never)),
    onOutcome: overrides.onOutcome ?? (() => {}),
  };

  const listeners = new Set<(snapshot: QueueSnapshot) => void>();
  let replaying = false;
  let backoff = BACKOFF_BASE_MS;
  let timer: unknown = null;
  /** Device clock minus server clock, learned from the `Date` response header.
   *  `null` until a server answers, and honestly reported as such. */
  let clockOffsetMs: number | null = null;

  const describe = (row: QueueRow): HeldItem => ({
    queueId: row.id,
    upn: rowUpn(row),
    clientCaptureId: row.reading.clientCaptureId,
    capturedAtDevice: row.reading.capturedAtDevice,
    context: row.context ?? {},
  });

  const publish = (snapshot: QueueSnapshot): QueueSnapshot => {
    for (const listener of listeners) listener(snapshot);
    return snapshot;
  };

  /**
   * With nobody signed in NOTHING is held: we can neither attribute nor
   * authenticate, so every row is simply still queued and waiting for a
   * sign-in. Reporting a hold here would put a conflict banner on a wall
   * display about a queue that has no conflict.
   */
  /**
   * Captures the server refused, until somebody has seen them.
   *
   * ⚠⚠ THIS EXISTS BECAUSE `onOutcome` WAS A NO-OP IN THE ONLY BUILD THAT SHIPS.
   * It defaults to `() => {}` and the sole production constructor supplies no
   * override, so the one channel a server refusal had back to the device went
   * nowhere: the row was deleted, the chip said synced, and an operator whose
   * capture was refused for competency, calibration or readiness saw a green tick
   * and walked away. The server had stored the refusal — rule 4 — and the person
   * who took the reading could not find that out, which is rule 4's client half.
   *
   * The callback stays as an extension point, but the surfacing no longer depends
   * on anyone remembering to supply it. Refusals ride the snapshot every
   * subscriber already receives.
   *
   * Capped, because this is a banner and not a log: on a device that has been
   * offline through a whole shift with a lapsed competency, every queued reading
   * comes back refused at once, and a list of two hundred is not something
   * anybody reads at a changeover. The count is what matters after the first few.
   */
  const refusals: QueueRefusal[] = [];
  let refusalsDropped = 0;
  const MAX_REFUSALS = 20;

  const recordRefusal = (outcome: CaptureOutcome | null, row: QueueRow): void => {
    if (refusals.length >= MAX_REFUSALS) {
      refusalsDropped += 1;
      return;
    }
    refusals.push({
      clientCaptureId: row.reading.clientCaptureId,
      parameterCode: row.context?.parameterCode ?? null,
      parameterName: row.context?.parameterName ?? null,
      reason: outcome?.rejectReason ?? null,
      // `error` is the payload-level refusal, which never reached the gate and so
      // has no rejectReason — it is still the only sentence that capture will
      // ever get, and dropping it would leave a blank row in the banner.
      message: outcome?.message ?? outcome?.error ?? null,
      at: deps.now(),
    });
  };

  const split = (rows: QueueRow[], photos: QueuePhotoRow[], me: string): QueueSnapshot => {
    const held = me ? rows.filter((row) => !mineToSend(row, me)) : [];
    // Signed out: nothing is held, every photo is simply waiting, as readings are.
    const myPhotos = me ? photos.filter((p) => mineToSend(p, me)) : photos;
    const photosPending = myPhotos.filter((p) => p.status === 'pending').length;
    return {
      queued: rows.length - held.length + photosPending,
      held: held.map(describe),
      // A copy, not the array itself: a subscriber holding the live reference
      // would see it mutate underneath React and never re-render.
      refusals: [...refusals],
      refusalsDropped,
      photosPending,
      photosRefused: myPhotos
        .filter((p) => p.status === 'refused')
        .map((p) => ({
          photoKey: p.photoKey,
          parameterName: p.context?.parameterName ?? p.context?.parameterCode ?? null,
          reason: p.reason,
          message: p.message ?? 'The server did not take this photo.',
        })),
      photosHeld: photos.length - myPhotos.length,
    };
  };

  /** The store could not be read: every count unknown, refusals still carried. */
  const unreadable = (): QueueSnapshot => ({
    queued: null,
    held: [],
    refusals: [...refusals],
    refusalsDropped,
    photosPending: null,
    photosRefused: [],
    photosHeld: 0,
  });

  /** Captures and photos, read together so a snapshot never counts half. */
  const readAll = async (): Promise<[QueueRow[], QueuePhotoRow[]]> => [
    await deps.idb.all(),
    await deps.idb.allPhotos(),
  ];

  /** Reading ids the server has answered with this session, by client id — so a
   *  photo added a moment after its reading left the device still finds it. */
  const knownReadingIds = new Map<string, number>();

  const noteServerClock = (response: Response): void => {
    const header = response.headers?.get?.('date');
    const serverMs = header ? Date.parse(header) : NaN;
    if (!Number.isNaN(serverMs)) clockOffsetMs = deps.now() - serverMs;
  };

  async function enqueue(draft: CaptureDraft): Promise<QueueRow> {
    const at = new Date(deps.now());
    const reading: CapturePayload = {
      clientCaptureId: deps.newId(),
      runId: draft.runId,
      specParameterId: draft.specParameterId,
      meterId: draft.meterId ?? null,
      values: draft.values,
      capturedAtDevice: at.toISOString(),
      // Always QUEUED, even with a live link: every capture goes through this
      // store, and a row that sat in IndexedDB — for a shift or for 200ms — was
      // queued. Claiming ONLINE because the network happened to be up would put
      // a small untruth in a column an auditor reads as provenance.
      captureMode: 'QUEUED',
      clockOffsetMs,
      evidenceRef: draft.evidenceRef ?? null,
    };

    // ⚠ THE STAMP GOES ON HERE, IN THE SAME EXPRESSION AS THE WRITE, AND IT IS
    // READ FROM THE SESSION. `enqueue` is the only writer and it never consults
    // the draft for an identity, so there is no path that queues an item
    // attributed to anyone but the account signed in at the moment of capture.
    // Anything a caller passed is overwritten, not honoured.
    const row: Omit<QueueRow, 'id'> = {
      reading,
      context: draft.context ?? {},
      enqueuedAt: at.toISOString(),
      upn: normaliseUpn(deps.getUpn()),
    };

    const id = await deps.idb.add(row, photoRowsFor(draft.photos, row));
    const stored: QueueRow = { ...row, id };
    publish(split(...(await readAll()), normaliseUpn(deps.getUpn())));
    return stored;
  }

  /** The draft's photos as stored rows: the reading's own client id and its own
   *  stamp (`row.upn`, read from the session in the write above), never a caller's. */
  function photoRowsFor(photos: readonly CapturePhoto[] | undefined, row: Omit<QueueRow, 'id'>): QueuePhotoRow[] {
    return (photos ?? []).map((p) => ({
      photoKey: deps.newId(),
      clientCaptureId: row.reading.clientCaptureId,
      readingId: null,
      upn: row.upn,
      blob: p.blob,
      capturedAtDevice: p.capturedAtDevice ?? row.reading.capturedAtDevice,
      status: 'pending',
      reason: null,
      message: null,
      context: row.context,
    }));
  }

  async function addPhoto(target: string | number, photo: CapturePhoto, context: CaptureContext = {}): Promise<QueuePhotoRow> {
    const clientCaptureId = typeof target === 'string' ? target : null;
    const readingId =
      typeof target === 'number' ? target : clientCaptureId ? (knownReadingIds.get(clientCaptureId) ?? null) : null;
    const stored: QueuePhotoRow = {
      photoKey: deps.newId(),
      clientCaptureId,
      readingId,
      blob: photo.blob,
      capturedAtDevice: photo.capturedAtDevice ?? new Date(deps.now()).toISOString(),
      status: 'pending',
      reason: null,
      message: null,
      context,
      // ⚠ THE STAMP, from the session, last - as on a reading.
      upn: normaliseUpn(deps.getUpn()),
    };
    await deps.idb.putPhoto(stored);
    publish(await snapshot());
    return stored;
  }

  /** The photos of a capture the server has just answered learn its reading id. */
  async function photosAnswered(clientCaptureId: string, readingId: number): Promise<void> {
    knownReadingIds.set(clientCaptureId, readingId);
    for (const p of await deps.idb.allPhotos()) {
      if (p.clientCaptureId === clientCaptureId && p.readingId !== readingId) {
        await deps.idb.putPhoto({ ...p, readingId });
      }
    }
  }

  /** A capture that got no reading row at all: its photos have nothing to belong to. */
  async function dropPhotosOf(clientCaptureId: string): Promise<void> {
    for (const p of await deps.idb.allPhotos()) {
      if (p.clientCaptureId === clientCaptureId && p.readingId === null) await deps.idb.deletePhoto(p.photoKey);
    }
  }

  async function refusePhoto(p: QueuePhotoRow, reason: string | null, message: string): Promise<void> {
    await deps.idb.putPhoto({ ...p, status: 'refused', reason, message });
  }

  type PhotoStop = 'done' | 'network' | 'server_error' | 'auth' | 'store_unavailable';

  /**
   * This account's photos whose reading the server has, one request each. Never
   * touches a reading. A drop or a 5xx waits for the next replay with the
   * queue's backoff; a 4xx marks the photo refused with the server's reason.
   */
  async function sendPhotos(me: string): Promise<PhotoStop> {
    let photos: QueuePhotoRow[];
    let rows: QueueRow[];
    try {
      photos = (await deps.idb.allPhotos())
        .filter((p) => p.status === 'pending' && mineToSend(p, me))
        .sort((a, b) => a.capturedAtDevice.localeCompare(b.capturedAtDevice));
      // ⚠ THE CAPTURES ARE READ HERE, AFTER THE PHOTOS, never handed in from the readings loop.
      // A reading and its photos are written in ONE transaction (`add`), so any photo read above
      // has its capture visible below. Captures read BEFORE the photos missed a commit landing in
      // between, and that photo was refused 'reading_unknown' and the operator told to discard
      // evidence whose reading was about to be sent (audit 28 Oct 2026). Only this replay deletes
      // a capture, and only before this point.
      rows = await deps.idb.all();
    } catch {
      return 'store_unavailable';
    }
    for (const p of photos) {
      const readingId =
        p.readingId ?? (p.clientCaptureId ? (knownReadingIds.get(p.clientCaptureId) ?? null) : null);
      try {
        if (readingId === null) {
          // Its reading is still on the device (perhaps held): wait for it.
          if (rows.some((r) => r.reading.clientCaptureId === p.clientCaptureId)) continue;
          // Neither on the device nor answered this session: say so, never guess.
          await refusePhoto(
            p,
            'reading_unknown',
            'The reading this photo belongs to could not be found on this device. Discard the photo.',
          );
          continue;
        }
      } catch {
        return 'store_unavailable';
      }

      let token: string | null;
      try {
        token = await deps.getToken();
      } catch {
        token = null;
      }
      if (!token) return 'auth';

      let response: Response;
      try {
        response = await deps.fetch(
          `${deps.endpoint}/${readingId}/photos?${new URLSearchParams({ capturedAtDevice: p.capturedAtDevice })}`,
          {
            method: 'POST',
            headers: { 'Content-Type': p.blob.type || 'image/jpeg', Authorization: `Bearer ${token}` },
            body: p.blob,
          },
        );
      } catch {
        return 'network';
      }
      if (response.status === 401) return 'auth';
      if (response.status >= 500) return 'server_error';
      const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;
      const reason = typeof body?.reason === 'string' ? body.reason : null;
      // A 403 with no reason is the session, not the photo: keep it and stop.
      if (response.status === 403 && reason === null) return 'auth';
      try {
        if (response.ok) {
          await deps.idb.deletePhoto(p.photoKey);
        } else {
          await refusePhoto(
            p,
            reason,
            typeof body?.error === 'string' ? body.error : `The server did not take the photo (${response.status}).`,
          );
        }
      } catch {
        return 'store_unavailable';
      }
    }
    return 'done';
  }

  /**
   * ⚠ A REPLAY ASKED FOR WHILE ONE RUNS IS OWED, NOT DROPPED. The running pass reads the readings
   * afresh each turn but its photos only once, at the end, so a reading enqueued or a photo added
   * while a photo uploads (seconds, on plant wifi) was never looked at: the caller's replay
   * answered 'already_running', the running one finished 'drained', and nothing else came - the
   * last answer of a round sat on the tablet with no error, Close run and Hand over refused, and
   * the line looked stuck (audit 28 Oct 2026). So the running pass goes round once more.
   */
  let replayOwed = false;
  /** Stops after which another pass now would only repeat the answer: the backoff timer (network,
   *  5xx) or a new sign-in (auth) is what tries again. */
  const NO_RERUN: ReadonlySet<ReplayStop> = new Set<ReplayStop>(['network', 'server_error', 'auth', 'store_unavailable']);

  async function replay(): Promise<ReplaySummary> {
    // The online event, the boot-time replay and the backoff timer can all fire
    // within the same tick. Without this guard they would POST the same row
    // more than once; the server's ClientCaptureId dedupe would save the data,
    // but the second request is still a write we never meant to make.
    if (replaying) {
      replayOwed = true;
      return { ...(await snapshot()), sent: 0, stoppedBecause: 'already_running' };
    }
    replaying = true;
    try {
      let summary: ReplaySummary;
      let sent = 0;
      do {
        replayOwed = false;
        summary = await replayPass();
        sent += summary.sent;
      } while (replayOwed && !NO_RERUN.has(summary.stoppedBecause));
      return { ...summary, sent };
    } finally {
      replayOwed = false;
      replaying = false;
    }
  }

  async function replayPass(): Promise<ReplaySummary> {
    let sent = 0;

    const stop = (reason: ReplayStop, state: QueueSnapshot): ReplaySummary => ({
      ...state,
      sent,
      stoppedBecause: reason,
    });

    for (;;) {
      let rows: QueueRow[];
      let photos: QueuePhotoRow[];
      try {
        [rows, photos] = await readAll();
      } catch {
        // Storage is gone or blocked (private browsing, a corrupt store). We
        // cannot see the queue, so we must not claim it is empty.
        // Refusals still ride along: they live in memory, not in the store,
        // and a device whose IndexedDB has gone read-only must not also
        // swallow the news that the server refused a capture.
        return stop('store_unavailable', unreadable());
      }

      const me = normaliseUpn(deps.getUpn());
      const state = publish(split(rows, photos, me));

      /**
       * Nothing (more) of this account's readings to send: now its photos,
       * whose readings are in. They never change why the replay stopped — a
       * photo is not a reading — but one waiting on the network or a 5xx keeps
       * the backoff growing, and only a clean pass resets it.
       */
      const afterReadings = async (reason: ReplayStop): Promise<ReplaySummary> => {
        const photoStop = me ? await sendPhotos(me) : 'done';
        if (photoStop === 'network' || photoStop === 'server_error') scheduleReplay();
        else if (reason === 'drained') backoff = BACKOFF_BASE_MS;
        return stop(reason, publish(await snapshot()));
      };

      if (rows.length === 0) return afterReadings('drained');
      // Quiet: not a conflict, not announced as one.
      if (!me) return stop('nobody_signed_in', state);

      // The first item belonging to THIS account — not the head of the queue.
      // Taking the head unconditionally is what stranded the person at the
      // machine behind somebody else's orphan.
      const row = rows.find((candidate) => mineToSend(candidate, me));
      if (!row) return afterReadings('nothing_for_this_account');

      // A token this session cannot mint is the same answer as a 401: the
      // capture is real and what is wrong is the session, so KEEP the row and
      // stop draining. Two shapes of that, both handled here — `acquireApiToken`
      // THROWS rather than returning null, and an escaped rejection would take
      // out the whole replay with no backoff armed and nothing on any screen.
      let token: string | null;
      try {
        token = await deps.getToken();
      } catch {
        token = null;
      }
      // Never send a capture without the bearer it is attributed by. An
      // unauthenticated POST can only come back 401 and the row would be kept
      // one round trip later anyway — but a reading that reached the server
      // without a token is a reading with nobody's name on it.
      if (!token) return stop('auth', state);

      let response: Response;
      try {
        response = await deps.fetch(deps.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          // One item per request. Batching would make the keep/delete decision
          // a per-item question answered by one HTTP status, and the first
          // partial failure would either re-send accepted readings or destroy
          // unsent ones.
          body: JSON.stringify({ readings: [row.reading] }),
        });
      } catch {
        // The network is down or the request never left. KEEP the row.
        scheduleReplay();
        return stop('network', state);
      }

      noteServerClock(response);

      // A 5xx is the server's problem, not the reading's. Deleting on one
      // destroys a capture that was never stored anywhere.
      if (response.status >= 500) {
        scheduleReplay();
        return stop('server_error', state);
      }

      // Expired or refused auth. KEEP the row and stop draining: the operator
      // signs in again and the queue releases itself. Deleting here would make
      // the "held locally" promise on the chip a lie.
      if (response.status === 401 || response.status === 403) {
        deps.onOutcome(null, row, response.status);
        return stop('auth', state);
      }

      // Any other answer is definitive — including a REJECTED reading, which
      // the server HAS. It is a red row in the run view, not a lost one.
      const outcome = await readOutcome(response, row.reading.clientCaptureId);
      sent += 1;
      backoff = BACKOFF_BASE_MS;
      deps.onOutcome(outcome, row, response.status);

      // ⚠ RECORDED BEFORE THE ROW IS DELETED, which is the only moment this
      // information exists on the device. A REJECTED reading is one the server
      // HAS and refused; a payload-level `error` never reached the gate. Both
      // are refusals from the operator's point of view and both have to be
      // shown, because the alternative — a row that quietly disappears — is the
      // silent loss the whole app is built against.
      if (outcome?.ingestVerdict === 'REJECTED' || outcome?.error) {
        recordRefusal(outcome, row);
      }

      try {
        // ⚠ THE PHOTOS LEARN THE READING'S ID BEFORE ITS ROW IS DELETED, which
        // is the last moment the device knows it. A REJECTED reading HAS a row
        // (rule 4) and keeps its photo: it is the evidence of what the operator
        // saw when the gate refused them. Only a capture with no row at all — a
        // payload-level error, or readingId 0 — loses its photos.
        const cid = row.reading.clientCaptureId;
        const readingId = typeof outcome?.readingId === 'number' ? outcome.readingId : null;
        if (outcome?.error || readingId === 0) await dropPhotosOf(cid);
        else if (readingId !== null && readingId > 0) await photosAnswered(cid, readingId);
        // An answer with no id: the photos stay, and sendPhotos says it cannot place them.
        await deps.idb.delete(row.id);
      } catch {
        // The server has this reading but the device could not forget it.
        // Carrying on would read the same row back at the top of the loop and
        // POST it again, for ever, against storage that has gone read-only —
        // the ClientCaptureId dedupe would hold the data, but the requests
        // would not stop. Stop instead and let the next replay find out
        // whether storage recovered.
        return stop('store_unavailable', state);
      }
    }
  }

  /**
   * Read the server's verdict for the item we just sent.
   *
   * The key is `outcomes` — that is what `quality_reading_capture` answers with,
   * one entry per submitted reading, alongside `capturedByUpn` and a `summary`.
   * Guessing at a different key is not a cosmetic slip: every answer parses to
   * null, `onOutcome` is handed nothing, and a REJECTED reading that the server
   * DID store never turns red in the run view. A refusal nobody can see is the
   * slower silent loss this whole app is built against.
   *
   * Tolerant beyond that, on purpose. The keep-or-delete decision is made on the
   * HTTP status alone, above; this is only the detail the run view displays, and
   * a body we cannot parse must not turn a stored reading into a retried one.
   */
  async function readOutcome(
    response: Response,
    clientCaptureId: string,
  ): Promise<CaptureOutcome | null> {
    const body = await response.json().catch(() => null);
    if (!body || typeof body !== 'object') return null;
    const record = body as Record<string, unknown>;
    const asOutcome = (value: unknown): Partial<CaptureOutcome> | null =>
      value !== null && typeof value === 'object' ? (value as Partial<CaptureOutcome>) : null;

    // One item per request, so there is one outcome — but match it by id rather
    // than trusting position, and fall back to the body itself for an error
    // envelope (`{ error: … }` from a 4xx) that carries no array at all.
    const list: unknown[] = Array.isArray(record.outcomes) ? record.outcomes : [record];
    const items = list
      .map(asOutcome)
      .filter((item): item is Partial<CaptureOutcome> => item !== null);
    const outcome = items.find((item) => item.clientCaptureId === clientCaptureId) ?? items[0];

    // Neither a verdict nor a sentence: a body we do not recognise. Say nothing
    // rather than report an outcome we invented.
    if (!outcome || (!outcome.ingestVerdict && !outcome.error)) return null;

    // The local id last: the server echoes the same value, but an item whose
    // payload it refused comes back with a null one, and the row is keyed by ours.
    return { ...outcome, clientCaptureId };
  }

  async function snapshot(): Promise<QueueSnapshot> {
    try {
      return split(...(await readAll()), normaliseUpn(deps.getUpn()));
    } catch {
      // ⚠ Storage is unreadable, so the counts are unknown rather than zero —
      // see the follow-up note in the module header. The REFUSALS are still
      // carried, because they live in memory and not in the store: a device
      // whose IndexedDB has gone read-only must not also swallow the news that
      // the server refused a capture.
      return unreadable();
    }
  }

  /** The owner's own unsent photo, or null. */
  async function ownPhoto(photoKey: string, ownerUpn: string): Promise<QueuePhotoRow | null> {
    const p = (await deps.idb.allPhotos()).find((x) => x.photoKey === photoKey);
    return p && rowUpn(p) !== '' && rowUpn(p) === normaliseUpn(ownerUpn) ? p : null;
  }

  async function retryPhoto(photoKey: string, ownerUpn: string): Promise<void> {
    const p = await ownPhoto(photoKey, ownerUpn);
    if (!p || p.status !== 'refused') return;
    await deps.idb.putPhoto({ ...p, status: 'pending', reason: null, message: null });
    await replay();
  }

  async function discardPhoto(photoKey: string, ownerUpn: string): Promise<void> {
    const p = await ownPhoto(photoKey, ownerUpn);
    if (!p) throw new Error(`Refusing to discard photo ${photoKey}: it is not yours, or it has already gone.`);
    await deps.idb.deletePhoto(photoKey);
    publish(await snapshot());
  }

  async function pendingForMe(): Promise<number | null> {
    return (await snapshot()).queued;
  }

  async function discardHeld(queueId: number, ownerUpn: string): Promise<void> {
    const rows = await deps.idb.all();
    const row = rows.find((candidate) => candidate.id === queueId);
    if (!row) return;
    // Naming the owner is not ceremony. The banner lists held items by account,
    // and discarding a capture is irreversible — a caller that cannot say whose
    // row it is asking to destroy does not get to destroy it.
    if (rowUpn(row) !== normaliseUpn(ownerUpn)) {
      throw new Error(
        `Refusing to discard queue item ${queueId}: it belongs to ` +
          `${rowUpn(row) || 'an account this device never recorded'}, not ${normaliseUpn(ownerUpn)}.`,
      );
    }
    await deps.idb.delete(queueId);
    // Its photos go with it: they are of a capture the server never received.
    await dropPhotosOf(row.reading.clientCaptureId);
    publish(await snapshot());
  }

  function scheduleReplay(): void {
    deps.clearTimer(timer);
    timer = deps.setTimer(() => {
      void replay();
    }, backoff);
    backoff = Math.min(BACKOFF_CAP_MS, backoff * 2);
  }

  function resetBackoff(): void {
    backoff = BACKOFF_BASE_MS;
  }

  function acknowledgeRefusals(clientCaptureIds: string[]): void {
    const seen = new Set(clientCaptureIds);
    for (let i = refusals.length - 1; i >= 0; i -= 1) {
      if (seen.has(refusals[i].clientCaptureId)) refusals.splice(i, 1);
    }
    // Only meaningful alongside the list it counts; once the list is empty the
    // number has nothing left to qualify.
    if (refusals.length === 0) refusalsDropped = 0;
    void snapshot().then(publish);
  }

  return {
    enqueue,
    replay,
    snapshot,
    pendingForMe,
    discardHeld,
    acknowledgeRefusals,
    addPhoto,
    retryPhoto,
    discardPhoto,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    scheduleReplay,
    resetBackoff,
    backoffMs: () => backoff,
  };
}

/* ── The app's singleton ──────────────────────────────── */

let singleton: CaptureQueue | null = null;

/** Built on first use, never at import: constructing it opens IndexedDB, and a
 *  unit test that imports this module must not need a browser to do so. */
export function captureQueue(): CaptureQueue {
  if (!singleton) singleton = createCaptureQueue();
  return singleton;
}
